export const getSubdomain = (domain = '') => {
  const domainParts = domain.split('.');
  if (domainParts.length > 3) {
    return domainParts[0];
  }
  return '';
};

export const isPreprodEnv = (domain = '') => {
  const subDomain = getSubdomain(domain);
  if (subDomain.includes('gutenberg')) {
    return true;
  }
  // not familiar enough w/ the SBSEG domains to handle their logic.
  if (
    domain.includes('turbotax') &&
    subDomain &&
    subDomain !== 'prod-staging' &&
    subDomain !== 'weborchestrator-prd' &&
    subDomain !== 'blog'
  ) {
    return true;
  }
  return false;
};

export const handleTestCookieName = (cookieName = '', domain = '') => {
  if (cookieName === 'tms' && isPreprodEnv(domain)) {
    const subDomain = getSubdomain(domain);
    return `tms_${subDomain}`;
  }
  return cookieName;
};

// https://stackabuse.com/get-query-string-values-in-javascript/
export const getQueryParamByName = (name, url) => {
  if (!name || !url) {
    return '';
  }
  const urlObj = new URL(url);
  const { searchParams } = urlObj;
  return searchParams.get(name) || '';
};

export const setIuxLocaleMeta = (locale = '') => {
  const htmlTag = document.getElementsByTagName('html')[0];
  if (locale && htmlTag) {
    const meta = document.createElement('meta');
    meta.name = 'ius-locale';
    meta.content = locale;
    htmlTag.appendChild(meta);
  }
};

export const iusWidgetIsReady = (intuit) =>
  !!(intuit && intuit.ius && intuit.ius.simpleSignIn && intuit.ius.simpleSignIn.setup);

export const widgetParams = (doc = {}, config = {}) => {
  let signInRedirectUrl = config.signInUrl || '';
  let ecosystemLearnMoreLink = config.learnMoreURL || '';
  let signUpLink = config.signUpLink || '';
  const htmlTag = (doc.getElementsByTagName && doc.getElementsByTagName('html')[0]) || {};
  const { locale } = window?.appVars || htmlTag?.dataset || '';
  if (locale && locale.toLowerCase() !== 'en_us' && locale.toLowerCase() !== 'en-us') {
    const localeToPass = locale.toLowerCase().replace('_', '-');
    setIuxLocaleMeta(localeToPass);
    signInRedirectUrl = signInRedirectUrl.concat(`?locale=${localeToPass}`);
    ecosystemLearnMoreLink = ecosystemLearnMoreLink.concat(`?locale=${localeToPass}`);
    signUpLink = signUpLink.concat(`?locale=${localeToPass}`);
  }

  return {
    enableAccountChoices: false, // this was hard coded to false in the legacy implmentation.
    offeringEnv: 'prd', // this was hard coded to prod in the legacy implmentation.
    offeringZone: config.offeringZone,
    signInRedirectUrl,
    isHorizontalLayout: config.layout === 'horizontal',
    ecosystemLearnMoreLink,
    signUpLink,
  };
};

/// originally oii simple signin.js
// ported from: https://github.intuit.com/cms-client-catalog/cg_ttcom_catalog/blob/master/workspace/lib/js/oii-simple-signin.js

const oiiSimpleSignIn = (win, doc, config) => {
  const myPrivate = {};
  const pub = {};
  let { testCookieName } = config;
  testCookieName = handleTestCookieName(testCookieName, win.location.hostname);
  const getCookie = (name) => {
    const re = new RegExp(`${name}=([^;]+)`);
    const value = re.exec(doc.cookie);
    return value != null ? decodeURI(value[1]) : null;
  };

  const testCookieValue = testCookieName && getCookie(testCookieName);
  // const logger = win.logger || win?.coreServiceAdapter?.logger || {};
  const beacon = win.analytics || win?.coreServiceAdapter?.analytics || {};

  const signInButtonId = 'ius-simple-sign-in-submit-btn';
  const knownDeviceCtaId = 'ius-known-device-cta';

  myPrivate.authWidgetSignInSuccess = () => {
    myPrivate.redirectWithTimeout(`${myPrivate.signInUrl}?pg=sso`);
  };

  myPrivate.authWidgetSignInFail = (message, redirect) => {
    if (beacon.trackEvent) {
      beacon.trackEvent({
        action: 'failed',
        object: 'task',
        ui_action: 'failed',
        ui_object: 'task',
        ui_object_detail: 'failed_signin',
        ui_access_point: 'simpleSignIn', // message.responseMessage,
        task_name: 'sign_in',
        error_type: message.httpStatus,
        error_detail: message.responseDetail,
        error_code: message.responseCode,
      });
    }
    // Redirect to accounts and let user login there
    myPrivate.updateRedirectUrl(redirect);
  };

  myPrivate.contentOverride = () => myPrivate.contentOverrides;

  myPrivate.onAdaptiveSignInSuccess = (message) => {
    if (message && beacon.trackEvent) {
      beacon.trackEvent({
        action: 'completed',
        object: 'task',
        ui_action: 'completed',
        ui_object: 'task',
        ui_object_detail: 'completed_signin',
        ui_access_point: 'simpleSignIn',
        task_name: 'sign_in',
      });
    }
  };

  /* eslint-disable consistent-return */
  myPrivate.waitForElm = (selector) => {
    return new Promise((resolve) => {
      if (doc.querySelector(selector)) {
        return resolve(doc.querySelector(selector));
      }

      const observer = new MutationObserver(() => {
        if (doc.querySelector(selector)) {
          resolve(doc.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(doc.body, {
        childList: true,
        subtree: true,
      });
    });
  };
  /* eslint-enable consistent-return */

  myPrivate.handleKnownDeviceCtaClick = () => {
    myPrivate.waitForElm(`#${knownDeviceCtaId}`).then((knownDeviceCtaEle) => {
      knownDeviceCtaEle.classList.add('suppress-default-beacon');
      knownDeviceCtaEle.addEventListener('click', () => {
        const beaconObject = {
          action: 'started',
          object: 'task',
          object_detail: 'com-auth-widget',
          ui_action: 'started',
          ui_object: 'task',
          ui_object_detail: 'started_signin',
          ui_access_point: 'idFirst',
          task_name: 'known_device_sign_in',
        };
        if (beacon.trackEvent) {
          beacon.trackEvent(beaconObject);
        }
      });
    });
  };

  myPrivate.authWidgetOnLoad = () => {
    const elementSelectorsForAnalyticsTracking = myPrivate.idsOfElementsForClickTracking || [];
    myPrivate.handleKnownDeviceCtaClick();

    elementSelectorsForAnalyticsTracking.forEach((elementID) => {
      const element = doc.getElementById(elementID);

      if (element && beacon.trackEvent) {
        element.addEventListener('click', () => {
          const text = element.innerText;

          let beaconObject = {
            ui_action: 'clicked',
            ui_access_point: 'simpleSignIn',
            ui_object_detail: `clicked ${text}`,
            ui_object: element.tagName,
          };

          if (elementID === signInButtonId) {
            beaconObject = {
              action: 'started',
              object: 'task',
              ui_action: 'started',
              ui_object: 'task',
              ui_object_detail: 'start_signin',
              ui_access_point: 'simpleSignIn',
              task_name: 'sign_in',
            };
          }

          beacon.trackEvent(beaconObject);
        });
      }
    });
  };

  myPrivate.onSignInChallenge = (iamTicket, username, redirect) => {
    // Redirect to accounts and let user login there
    myPrivate.updateRedirectUrl(redirect);
  };

  myPrivate.updateRedirectUrl = (redirect) => {
    // use SEO friendly urls for the redirect
    let redirectURL = redirect.replace('index.html', 'turbotaxonline');

    const domain = win.location.host;

    // append redirect_url for redirect after successful login
    const redirectURLforParam = encodeURIComponent(
      `https://${domain}${myPrivate.signInUrl}?pg=sso`,
    );
    redirectURL = `${redirectURL}&redirect_url=${redirectURLforParam}`;

    // Redirect to accounts url and let user login with them
    myPrivate.redirectWithTimeout(redirectURL);
  };

  myPrivate.redirectWithTimeout = (redirectUrl) => {
    const redirectWaitMillis = parseInt(myPrivate.redirectTimeout, 10) || 0;

    // give time to the beacons to get to the server.
    setTimeout(() => {
      win.location = redirectUrl; // eslint-disable-line no-param-reassign
    }, redirectWaitMillis);
  };

  // todo -> this could use some modernization.
  myPrivate.getQueryParamUserId = () => {
    return getQueryParamByName('userId', win.location.href);
  };

  // Additional configuration options for Sign In:
  // https://github.intuit.com/pages/identity-authn/iux-hostedui-config/#/widgets/usage/sign-in?id=configuration-parameters
  // Simple Sign in:
  // https://github.intuit.com/identity-authn/identity-authn-core-ui/blob/b7e4588d05f67d1be40d6861201265b405b87774/src/iux-web/v2/scripts/ius-simple-sign-in-widget/index.js#L1063
  myPrivate.widgetParamDefaults = {
    showRememberMe: true,
    enableRememberMe: true,
    redirectOnError: '',
    enableAuthLinks: '',
    offeringId: config.offeringId, // this is required. no widget will show without it.
    offeringEnv: '',
    theme: config.theme,
    contentOverride: myPrivate.contentOverride,
    onLoad: myPrivate.authWidgetOnLoad,
    onViewDisplayed: () => {},
    errorMessageOverride: '',
    namespaceId: '',
    enableVerifyLogin: '',
    enableStrongAuth: '',
    enableOAuthFlow: '',
    isEcosystemTheme: true,
    iuxTests: testCookieValue,
    ecosystemLearnMoreLink: '',
    rememberMeExpires: '',
    persistCookie: '',
    showPin: '',
    signUpLink: config.signUpLink,
    signInHelpLink: '',
    contactUsLink: '',
    accountRecoveryLink: config.accountRecoveryLink,
    userIdRecoveryLink: '',
    passwordRecoveryLink: '',
    signInWithAnotherAccountLink: '',
    signUpAnotherAccountLink: '',
    userIdToPrefill: myPrivate.getQueryParamUserId(),
    isUserIdReadonly: '',
    onSignInSuccess: myPrivate.authWidgetSignInSuccess,
    onSignInFail: myPrivate.authWidgetSignInFail,
    onSignInChallenge: myPrivate.onSignInChallenge,
    onTransferSuccess: '',
    onTransferFail: '',
    onSetRealmSuccess: '',
    onSetRealmFail: '',
    onVerifySuccess: '',
    onOAuthExchangeComplete: '',
    onOAuthExchangeFail: '',
    onContinueWithThisAccountClick: '',
    cachePassword: '',
    convertProvisionalUser: '',
    enableSetRealm: '',
    onAdaptiveSignInSuccess: myPrivate.onAdaptiveSignInSuccess,
    onSendSignInConfirmationSuccess: '',
    onSendSignInConfirmationFail: '',
    onUpdateContactInfoSuccess: '',
    onUpdateContactInfoFail: '',
    onVerifySignInConfirmationSuccess: '',
    onVerifySignInConfirmationFail: '',
    customerSupportLink: '',
    privacyStatementLink: 'https://security.intuit.com/privacy/',
    externalFlowStartTime: performance.now(),
  };
  myPrivate.contentOverrides = [
    {
      id: 'ius-label-userid',
      content: 'User ID',
    },
    {
      id: 'ius-label-password',
      content: 'Password',
    },
    {
      id: 'ius-link-sign-up-prefix-text',
      content: 'New to TurboTax?',
    },
  ];
  myPrivate.fatalErrorMessage = 'iux fatal error';
  myPrivate.signInUrl = config.signIn;
  myPrivate.queryParamUserId = 'authFormUserID';
  myPrivate.redirectTimeout = 0;
  myPrivate.cookieExpirationDays = 3;
  myPrivate.adaptiveSignInChallengeKey = 'challenge';
  myPrivate.idsOfElementsForClickTracking = [
    'ius-link-terms-of-use',
    'ius-link-account-recovery',
    'ius-link-sign-up',
    'ius-link-account-recovery-bottom',
    signInButtonId,
    // knownDeviceCtaId, -> do not re-add. is manged via handleKnownDeviceCtaClick
  ];

  pub.initializeWidget = (params = {}) => {
    const setupParams = { ...myPrivate.widgetParamDefaults, ...params };
    win.intuit.ius.simpleSignIn.setup(setupParams);
  };

  return pub;
};

/// original init code
// ported from https://github.intuit.com/cms-client-catalog/cg_ttcom_catalog/blob/master/workspace/lib/js/auth-init.js

const initWidget = (win, doc, config) => {
  const params = widgetParams(doc, config);
  const oii = oiiSimpleSignIn(win, doc, config);
  oii.initializeWidget(params);
};

export const authInit = (win = {}, doc = {}, config = {}) => {
  const { intuit } = win;
  if (iusWidgetIsReady(intuit)) {
    initWidget(win, doc, config);
  } else {
    win.addEventListener('iusWidgetScriptsLoaded', () => {
      initWidget(win, doc, config);
    });
  }
};

export default authInit;
